// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dynamic Tabs Component
 *
 * Renders tabs based on UI layout configuration and node widgets.
 * Supports role-based visibility and dynamic widget loading.
 */

import { useState, useMemo, useCallback, useEffect, type FC } from 'react'
import { createPortal } from 'react-dom'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { getLayoutComponent } from '../../../core/layout-registry'
import type { UILayout, UILayoutTab } from '../types'
import type { NodeScreenPropsBase } from '../../../core/types'
import { FileText, Database, Loader2, Blocks } from 'lucide-react'
import { useWorkspaceRefresh } from '../hooks/useWorkspaceRefresh'

// Tab label → icon mapping (FileText is default for unlisted labels)
const TAB_ICONS: Record<string, React.ElementType> = {
  Knowledge: Database,
  Datasource: Database,
}

/**
 * Auto-refreshing widget wrapper
 * Automatically reloads when workspace:changed event fires
 */
interface NodeWidgetWrapperProps {
  WidgetComponent: React.ComponentType<NodeScreenPropsBase>
  orgId: string
  workspaceId: string
  agentId?: string
  nodeDefinition?: string
  nodeInstanceId?: string
  label?: string
  config?: Record<string, unknown>
  onLoadStart: () => void
  onLoadEnd: () => void
}

const NodeWidgetWrapper: FC<NodeWidgetWrapperProps> = ({
  WidgetComponent,
  orgId,
  workspaceId,
  agentId,
  nodeDefinition,
  nodeInstanceId,
  label,
  config,
  onLoadStart,
  onLoadEnd,
}) => {
  const [refreshKey, setRefreshKey] = useState(0)

  // Auto-refresh when workspace changes
  useWorkspaceRefresh(() => {
    setRefreshKey(prev => prev + 1)
  })

  return (
    <WidgetComponent
      key={refreshKey}
      orgId={orgId}
      workspaceId={workspaceId}
      agentId={agentId}
      nodeDefinition={nodeDefinition}
      nodeInstanceId={nodeInstanceId}
      label={label}
      config={config}
      onLoadStart={onLoadStart}
      onLoadEnd={onLoadEnd}
    />
  )
}

interface DynamicTabsProps {
  uiLayout?: UILayout
  userRole?: 'global_admin' | 'workspace_admin' | 'workspace_user'
  workspaceId: string
  organizationId?: string
  workspaceType?: 'system' | 'application' | 'workspace'
  onTabChange?: (tabId: string) => void
  renderMode?: 'full' | 'tabs-only' | 'content-only'  // Control what gets rendered
  activeTabId?: string  // Allow external control of active tab
  isMobile?: boolean  // Mobile-specific rendering
  // Portal target ID for rendering TabsList elsewhere in DOM while keeping it in the
  // same React context as TabsContent. This ensures Radix generates matching aria-controls
  // IDs for accessibility compliance (required for Lighthouse ARIA validation).
  tabsPortalId?: string
}

export const DynamicTabs: React.FC<DynamicTabsProps> = ({
  uiLayout,
  userRole = 'workspace_user',
  workspaceId,
  organizationId = '',
  workspaceType = 'workspace',
  onTabChange,
  renderMode = 'full',
  activeTabId,
  isMobile = false,
  tabsPortalId,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>('')
  const [loadingWidgets, setLoadingWidgets] = useState<Set<string>>(new Set())

  // Filter by role permissions, then sort by order
  const visibleTabs = useMemo(() => {
    if (!uiLayout?.tabs) return []

    const canSee = (tab: UILayoutTab) =>
      userRole === 'global_admin' ||
      !tab.permissions?.length ||
      tab.permissions.includes(userRole)

    return [...uiLayout.tabs]
      .filter(canSee)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [uiLayout, userRole])

  // Resolve active tab: external prop → internal state → default
  // Falls back when the URL tab doesn't exist in this workspace (e.g. after switching workspaces)
  const visibleTabIds = useMemo(() => new Set(visibleTabs.map(t => t.id)), [visibleTabs])
  const activeTab = (activeTabId && visibleTabIds.has(activeTabId))
    ? activeTabId
    : internalActiveTab && visibleTabIds.has(internalActiveTab)
      ? internalActiveTab
      : ''

  // Set initial active tab or reset when workspace changes
  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      const defaultTab = uiLayout?.defaultTab ?? visibleTabs[0].id
      setInternalActiveTab(defaultTab)
      // Sync URL when falling back to default tab
      onTabChange?.(defaultTab)
    }
  }, [activeTab, visibleTabs, uiLayout, onTabChange])

  // Handle tab change
  const handleTabChange = useCallback((value: string) => {
    setInternalActiveTab(value)
    onTabChange?.(value)
  }, [onTabChange])

  // Render widget with loading state
  const renderWidget = useCallback((componentId: string, viewMode?: string, agentId?: string, nodeInstanceId?: string, widgetLabel?: string) => {
    const component = getLayoutComponent(componentId)
    if (!component) {
      return <div className="p-4 text-center text-muted-foreground">Component not found: {componentId}</div>
    }

    const WidgetComponent = viewMode === 'admin'
      ? component.ConfigComponent
      : component.Component

    if (!WidgetComponent) {
      return <div className="p-4 text-center text-muted-foreground">Widget not available for {component.display.label}</div>
    }

    const nodeDefinition = component.id

    // Application nodes render workspace context, no instance needed
    // Subsystem widgets need instanceId for data binding - log in dev only
    if (!nodeInstanceId && component.parentType !== 'application' && import.meta.env.DEV) {
      console.debug(
        `[DynamicTabs] Subsystem widget "${component.id}" has no instanceId. ` +
        'Backend may not have auto-discovered an instance.',
      )
    }

    const widgetKey = `${componentId}-${viewMode ?? 'default'}`
    const isLoading = loadingWidgets.has(widgetKey)

    return (
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        <NodeWidgetWrapper
          WidgetComponent={WidgetComponent}
          orgId={organizationId ?? ''}
          workspaceId={workspaceId}
          agentId={agentId}
          nodeDefinition={nodeDefinition}
          nodeInstanceId={nodeInstanceId}
          label={widgetLabel}
          onLoadStart={() => setLoadingWidgets(prev => new Set(prev).add(widgetKey))}
          onLoadEnd={() => setLoadingWidgets(prev => {
            const next = new Set(prev)
            next.delete(widgetKey)
            return next
          })}
        />
      </div>
    )
  }, [workspaceId, loadingWidgets, organizationId])

  // Render tab content - nodes provide their own Card styling
  const renderTabContent = useCallback((tab: UILayoutTab) => {
    const widgets = tab.widgets ?? []

    if (widgets.length === 0) {
      return (
        <div className="container mx-auto px-4 py-4">
          <p className="p-8 text-center text-muted-foreground">No widgets configured for this tab</p>
        </div>
      )
    }

    return (
      <div className="container mx-auto px-4 py-4 space-y-4">
        {widgets.map((widget, index) => (
          <div key={`${widget.component}-${index}`}>
            {renderWidget(widget.component, widget.view ?? undefined, widget.agentId ?? undefined, widget.instanceId ?? undefined, widget.label ?? undefined)}
          </div>
        ))}
      </div>
    )
  }, [renderWidget])

  // Render tabs based on user permissions and configuration

  if (visibleTabs.length === 0) {
    const isUnconfigured = !uiLayout || !uiLayout.tabs || uiLayout.tabs.length === 0
    const isPipelineWorkspace = workspaceType === 'system' || workspaceType === 'application'

    return (
      <div className="p-8 text-center text-muted-foreground">
        {isPipelineWorkspace ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <Blocks className="h-12 w-12 text-muted-foreground/50" />
            </div>
            <p className="text-lg font-medium text-foreground">Pipeline Configuration Workspace</p>
            <p className="text-sm max-w-md mx-auto">
              {workspaceType === 'system' ? 'System' : 'Application'} workspaces are for defining
              shared pipelines. Use the <span className="font-medium">Pipeline Builder</span> icon
              in the toolbar to create and edit pipelines.
            </p>
          </div>
        ) : isUnconfigured ? (
          <div className="space-y-2">
            <p className="text-lg font-medium text-foreground">Workspace Not Configured</p>
            <p className="text-sm">This workspace hasn't been set up yet.</p>
            {userRole === 'global_admin' && (
              <p className="text-sm mt-4">
                As an admin, you can configure this workspace by going to the Workflow page.
              </p>
            )}
          </div>
        ) : (
          <p>No tabs available for your role</p>
        )}
      </div>
    )
  }

  // Portal target for rendering tabs in a different DOM location
  const portalTarget = tabsPortalId ? document.getElementById(tabsPortalId) : null

  // Hide the tab selector when there's only one tab — nothing to switch between.
  const showTabsList = visibleTabs.length > 1

  // Build the TabsList element
  const tabsList = showTabsList && (renderMode === 'full' || renderMode === 'tabs-only') ? (
    isMobile ? (
      // Scrollable container with right fade hint
      <div className="relative w-full">
        <TabsList className="inline-flex h-10 w-full overflow-x-auto overflow-y-hidden scrollbar-hide bg-transparent border-0 rounded-none p-0 gap-1">
          {visibleTabs.map(tab => {
            const Icon = TAB_ICONS[tab.label] ?? FileText
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                data-testid={`workspace-tab-${tab.id}`}
                className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-xs rounded-full border border-slate-200 data-[state=active]:!bg-slate-900 data-[state=active]:!text-white data-[state=active]:!border-slate-900 data-[state=active]:!shadow-none"
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{tab.label}</span>
              </TabsTrigger>
            )
          })}
        </TabsList>
        {/* Fade hint that more tabs exist off-screen */}
        <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />
      </div>
    ) : (
      <TabsList className="w-full inline-flex h-auto justify-center bg-transparent border-0 rounded-none py-2 gap-1">
        {visibleTabs.map(tab => {
          const Icon = TAB_ICONS[tab.label] ?? FileText
          return (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              data-testid={`workspace-tab-${tab.id}`}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 text-slate-600 data-[state=active]:!bg-slate-900 data-[state=active]:!text-white data-[state=active]:!border-slate-900 data-[state=active]:!shadow-none"
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </TabsTrigger>
          )
        })}
      </TabsList>
    )
  ) : null

  return (
    <Tabs
      id="workspace-tabs"
      value={activeTab}
      onValueChange={handleTabChange}
      className="w-full"
    >
      {/* Render TabsList via portal if target exists, otherwise inline */}
      {tabsList && (portalTarget ? createPortal(tabsList, portalTarget) : tabsList)}

      {/* Tab content - only render in 'full' or 'content-only' mode */}
      {(renderMode === 'full' || renderMode === 'content-only') && (
        <div className="h-full">
          {/* cm-coding: Workspace dashboards can start polling and loading data
              as soon as they mount. Render only the active tab so hidden
              widgets cannot consume API capacity or slow the workspace view
              the user is currently operating. */}
          {visibleTabs.filter(tab => tab.id === activeTab).map(tab => (
            <TabsContent
              key={tab.id}
              value={tab.id}
              data-testid={`workspace-tabpanel-${tab.id}`}
              className="h-full focus:outline-none"
            >
              {renderTabContent(tab)}
            </TabsContent>
          ))}
        </div>
      )}

    </Tabs>
  )
}
